import closeIcon from "@/img/close-icon.svg";
import FavCityButton from "../fav-city-button/FavCityButton";
import location from "@/img/location-with-bar.svg";

import { IFav } from "@/hooks/use-favorites";
import { getCurrentLanguage } from "@/helpers/get-current-langage";
import { CityFeature } from "@/schemas/city-schema";
import { useMemo, useState } from "@wordpress/element";
import { useTranslation } from "react-i18next";
import { handleSetLocationId } from "@/helpers/url-params";
import { LocationType } from "@/types/location-params-type";

import "./style.scss";

type SearchModalProps = {
	locationType: LocationType;
	isOpen: boolean;
	cities: CityFeature[];
	favorites?: IFav[];
	showFav: boolean;
	placeholder: string;

	userPosition: {
		cityNameEn: string;
		cityNameAr: string;
		cityId: string;
	} | null;
	handleSelectLocation: (value: Pick<CityFeature, "properties">) => void;
	closeModal: () => void;
};

export const SearchModal = ({
	isOpen,
	cities,
	userPosition,
	locationType,
	favorites = [],
	placeholder,
	showFav,
	handleSelectLocation,
	closeModal,
}: SearchModalProps) => {
	const lang = getCurrentLanguage();
	const { t } = useTranslation();
	const [searchInput, setSearchInput] = useState<string>("");

	const citiesResult = useMemo(() => {
		return cities.filter((item) => {
			const name =
				lang === "ar" ? item?.properties.name_ar : item.properties.name_en;

			if (!name) return false;

			return name
				.toLowerCase()
				.trim()
				.startsWith(searchInput.toLowerCase().trim());
		});
	}, [cities, searchInput, lang]);

	const handleClickCity = (value: Pick<CityFeature, "properties">) => {
		const cityId = value.properties.id;

		handleSetLocationId({
			locationType,
			locationId: cityId,
		});

		handleSelectLocation(value);
		closeModal();
	};

	if (!isOpen) return null;

	return (
		<div
			className="search-modal"
			onClick={(e) => {
				if (e.target === e.currentTarget) {
					closeModal();
				}
			}}
		>
			<div className="modal-content">
				<button onClick={() => closeModal()}>
					<img src={closeIcon} alt="close search modal" />
				</button>

				<p className="modal-title">{t("selectLocation")}</p>

				<input
					type="text"
					placeholder={placeholder}
					onChange={(e) => setSearchInput(e.target.value)}
				/>

				<ul className="fav-list">
					{favorites.length > 0 && showFav
						? favorites.map((fav) => {
								const { nameEn, nameAr, id } = fav;
								return (
									<li
										key={fav.id}
										onClick={() =>
											handleClickCity({
												properties: {
													id,
													label: nameEn,
													name_en: nameEn,
													name_ar: nameAr,
												},
											})
										}
									>
										<FavCityButton
											locationNameEn={nameEn}
											locationNameAr={nameAr}
											locationId={id}
											showFavEmpty={true}
											isDisabled={false}
										/>

										<p>{lang === "ar" ? fav.nameAr : fav.nameEn}</p>
									</li>
								);
						  })
						: null}
				</ul>

				<ul className="cities-result">
					{userPosition && (
						<li
							className="user-position"
							onClick={() =>
								handleClickCity({
									properties: {
										id: userPosition.cityId,
										label: userPosition.cityNameEn,
										name_ar: userPosition.cityNameAr,
										name_en: userPosition.cityNameEn,
									},
								})
							}
						>
							<img src={location} alt="current location" />
							<p>
								{lang === "ar"
									? userPosition.cityNameAr
									: userPosition.cityNameEn}
							</p>
						</li>
					)}

					{citiesResult.length > 0 ? (
						citiesResult.map((cities) => {
							const { name_ar, name_en, id } = cities.properties;
							return (
								<li
									key={id}
									onClick={() =>
										handleClickCity({
											properties: {
												...cities.properties,
											},
										})
									}
								>
									<p>{lang === "ar" ? name_ar : name_en}</p>
								</li>
							);
						})
					) : (
						<p className="no-result">No result</p>
					)}
				</ul>
			</div>
		</div>
	);
};

export default SearchModal;
